/** Whether the given string is a plain http(s) URL — used to guard against stored `javascript:` etc. links. */
export function isSafeHttpUrl(url: string): boolean {
  return /^https?:\/\//i.test(url.trim());
}

/**
 * 업로드 저장소의 호스트. **R2에 커스텀 도메인을 붙였을 때만 필요하다.**
 *
 * 등록하는 곳은 둘이고, 넘기는 값은 같다(둘 다 공개 URL이라 비밀이 아니다):
 *  - Worker: `installR2Storage`가 `R2_PUBLIC_BASE`로 등록 (저장 시 검증용)
 *  - SPA: 부팅 때 `VITE_UPLOAD_BASE`로 등록 (영수증 미리보기 표시용)
 *
 * 한쪽만 등록하면 그쪽만 동작한다 — 서버만이면 미리보기가 안 뜨고, 클라이언트만이면
 * 저장할 때 URL이 버려진다. `.r2.dev` 개발 URL은 아래에 기본으로 들어 있어 등록이
 * 필요 없다.
 */
let extraUploadHost: string | null = null;

export function registerUploadHost(publicBase: string | undefined | null): void {
  if (!publicBase) return;
  try {
    extraUploadHost = new URL(publicBase).hostname;
  } catch {
    // 주소 모양이 아니면 무시한다 — 여기서 던지면 앱 부팅이 막힌다.
  }
}

/**
 * Whether `url` matches something this app's own upload routes can actually produce: the local
 * `/uploads/...` filesystem fallback, a Vercel Blob URL, or the Cloudflare R2 bucket this
 * deployment writes to. A client-supplied "already uploaded" field (e.g. receiptPhotoUrl) goes
 * through a Server Action, which can be called directly over the network — this rejects an
 * arbitrary external URL smuggled in that way, since it'll later be rendered as an
 * `<img src>`/`<a href>`. The `.blob.vercel-storage.com` suffix check matches @vercel/blob's own
 * URL-validation logic (see its `hostname.endsWith(".blob.vercel-storage.com")`); `.r2.dev` is
 * Cloudflare's own bucket domain, the R2 counterpart.
 *
 * **호스트 목록에 없으면 저장 시 버려진다.** R2에 커스텀 도메인을 붙였다면
 * `registerUploadHost`로 알려줘야 한다(위 주석 참고) — 안 그러면 영수증이 조용히
 * 사라진다.
 */
export function isOwnUploadUrl(url: string): boolean {
  if (url.startsWith("/uploads/")) return true;
  if (!isSafeHttpUrl(url)) return false;
  try {
    const { hostname } = new URL(url);
    return (
      hostname.endsWith(".blob.vercel-storage.com") ||
      hostname.endsWith(".r2.dev") ||
      hostname === extraUploadHost
    );
  } catch {
    return false;
  }
}

/**
 * A Google Maps search link for a free-text address. When a place name is
 * given, it's prefixed into the query ("query=<name>, <address>") — an
 * address alone is often ambiguous (multiple buildings on the same street),
 * while the name narrows the search to the actual place.
 */
export function googleMapsUrl(address: string, name?: string | null): string {
  const query = name ? `${name}, ${address}` : address;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}
